<template>
  <a-tooltip v-if="showTooltip" placement="top">
    <template #title>
      <div class="text-sm text_color">
        <div class="font-bold">{{ title }}</div>
        <div v-for="(timestamp, index) in timestamps" :key="index">
          {{ formatTimestamp(timestamp) }}
        </div>
        <span v-if="hasMoreTimestamps" class="text-xs secondary_color">
          ...
          {{ originalCount - timestamps.length }}
          more
        </span>
      </div>
    </template>
    <span
      class="flex items-center justify-center relative"
      :class="{
        engagement_active: isActive,
        engagement_inactive: !isActive,
      }"
      :id="`${source}_engagement_count`"
    >
      <img
        :src="iconData.activeIcon"
        :alt="iconData.activeAlt"
        v-if="isActive"
      />
      <ThemeImage
        :icon="iconData.inactiveIcon"
        :alt="iconData.inactiveAlt"
        v-else
      />
      <span
        v-if="showCount"
        class="absolute engagement_count leading-[1] text-[9px] -bottom-1 rounded-lg font-bold"
      >
        {{ count }}
      </span>
    </span>
  </a-tooltip>
  <span
    v-else
    class="flex items-center justify-center relative"
    :class="{
      engagement_active: isActive,
      engagement_inactive: !isActive,
    }"
    :id="`${source}_engagement_count`"
  >
    <img :src="iconData.activeIcon" :alt="iconData.activeAlt" v-if="isActive" />
    <ThemeImage
      :icon="iconData.inactiveIcon"
      :alt="iconData.inactiveAlt"
      v-else
    />
    <span
      v-if="showCount"
      class="absolute engagement_count leading-[1] text-[9px] -bottom-1 rounded-lg font-bold"
    >
      {{ count }}
    </span>
  </span>
</template>

<script setup>
import { computed } from "vue";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import imagePath from "@/src/paths/imagePath.js";
import { engagementTooltipTypes } from "@/src/constants/emailConstants.js";

const props = defineProps({
  showTooltip: Boolean,
  isActive: Boolean,
  timestamps: {
    type: Array,
    default: () => [],
  },
  title: String,
  type: {
    type: String,
    required: true,
    validator: (value) => Object.values(engagementTooltipTypes).includes(value),
  },
  count: {
    type: Number,
    default: 0,
  },
  showCount: Boolean,
  originalCount: Number,
  source: String,
});

const iconData = computed(() => {
  switch (props.type) {
    case engagementTooltipTypes.DELIVERED:
      return {
        activeIcon: imagePath.neutral.action.deliveredIconGreen,
        activeAlt: "Delivered",
        inactiveIcon: imageNames.DELIVERED_INACTIVE,
        inactiveAlt: "Not delivered",
      };
    case engagementTooltipTypes.OPENED:
      return {
        activeIcon: imagePath.neutral.action.seenIconGreen,
        activeAlt: "Opened",
        inactiveIcon: imageNames.SEEN_INACTIVE,
        inactiveAlt: "Not Opened",
      };
    case engagementTooltipTypes.CLICKED:
      return {
        activeIcon: imagePath.neutral.action.clickedIconGreen,
        activeAlt: "Clicked",
        inactiveIcon: imageNames.CLICKED_INACTIVE,
        inactiveAlt: "Not Clicked",
      };
    case engagementTooltipTypes.REPLIED:
      return {
        activeIcon: imagePath.neutral.action.repliedIconGreen,
        activeAlt: "Replied",
        inactiveIcon: imageNames.REPLIED_INACTIVE,
        inactiveAlt: "Not Replied",
      };
    case engagementTooltipTypes.SENT:
      return {
        activeIcon: imagePath.dark.selected.sentIconActive,
        activeAlt: "Sent",
        inactiveIcon: imageNames.SENT_INACTIVE,
        inactiveAlt: "Not Sent",
      };
    case engagementTooltipTypes.ACCEPTED:
      return {
        activeIcon: imagePath.acceptedIconActive,
        activeAlt: "Accepted",
        inactiveIcon: imageNames.ACCEPTED_INACTIVE,
        inactiveAlt: "Not Accepted",
      };
    default:
      return {
        activeIcon: "",
        activeAlt: "",
        inactiveIcon: "",
        inactiveAlt: "",
      };
  }
});

const hasMoreTimestamps = computed(() => {
  const original = props.originalCount || props.count;
  return original > props.timestamps.length;
});
</script>

<style scoped>
.engagement_active {
  border-radius: 100%;
  height: 1.7rem;
  width: 1.7rem;
  background-color: var(--engagement-active);
  padding: 0.4rem;
}

.engagement_inactive {
  border-radius: 0.5rem;
  height: 1.7rem;
  width: 1.7rem;
  background-color: var(--white-opacity-8);
  padding: 0.4rem;
}

.engagement_count {
  padding: 0.1rem 0.3rem;
  border-radius: 0.4rem;
  background-color: #276409;
  color: var(--engagement-count);
}
</style>
